import { memo, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { format, parseISO, isValid } from 'date-fns';

const formatDateString = (dateString) => {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, 'MMMM d, yyyy');
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const cellBaseStyle = {
  fontSize: '0.7rem',
  padding: '6px 12px',
  border: '1px solid #d9d9d9',
  verticalAlign: 'middle',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  height: '32px',
  boxSizing: 'border-box',
};

const cellLabelStyle = {
  ...cellBaseStyle,
  color: '#374151',
  fontWeight: 500,
  backgroundColor: '#f9fafb',
  minWidth: 155,
};

const cellValueStyle = {
  ...cellBaseStyle,
  color: '#111827',
  fontWeight: 600,
  backgroundColor: '#ffffff',
  minWidth: 150,
};

const ReportHeader = ({
  corporation,
  reportTitle,
  fromDate,
  toDate,
  isMultiCorp = false,
  selectedCorpCount,
  selectedPcCount,
  pcNo,
  reportType = 'daily',
  reportData,
  filters,
}) => {
  const rawGridDetails = useMemo(() => {
    if (reportData?.GridDetails && Array.isArray(reportData.GridDetails)) {
      return reportData.GridDetails;
    }
    if (Array.isArray(reportData)) {
      return reportData;
    }
    return [];
  }, [reportData]);

  const uniqueCorps = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return [];
    const map = new Map();
    rawGridDetails.forEach((r) => {
      const name = r.CorporationName || r.corpName;
      const id = r.CorporationID || r.corpId;
      if (
        name !== null &&
        name !== undefined &&
        String(name).trim() !== '' &&
        String(name).toLowerCase() !== 'null' &&
        String(name).toLowerCase() !== 'undefined'
      ) {
        const key = String(id || name).trim();
        if (!map.has(key)) {
          map.set(key, String(name).trim());
        }
      } else if (
        id !== null &&
        id !== undefined &&
        String(id).trim() !== '' &&
        String(id).toLowerCase() !== 'null' &&
        String(id).toLowerCase() !== 'undefined'
      ) {
        const key = String(id).trim();
        if (!map.has(key)) {
          map.set(key, String(id).trim());
        }
      }
    });
    return Array.from(map.values());
  }, [rawGridDetails]);

  // Derive multi-corp flag exclusively from API response when response is available
  const isMultiCorpFromResponse = useMemo(() => {
    if (uniqueCorps.length > 0) {
      return uniqueCorps.length > 1;
    }
    return Boolean(isMultiCorp);
  }, [uniqueCorps.length, isMultiCorp]);

  const uniquePcs = useMemo(() => {
    if (!rawGridDetails || rawGridDetails.length === 0) return [];
    const map = new Map();
    rawGridDetails.forEach((r) => {
      const pcId = r.PCID ?? r.pcId ?? r.ProfitCenterID;
      const pcName = r.PCName ?? r.pcName ?? r.ProfitCenterName;

      const hasValidId =
        pcId !== null &&
        pcId !== undefined &&
        String(pcId).trim() !== '' &&
        String(pcId).toLowerCase() !== 'null' &&
        String(pcId).toLowerCase() !== 'undefined';

      const hasValidName =
        pcName !== null &&
        pcName !== undefined &&
        String(pcName).trim() !== '' &&
        String(pcName).toLowerCase() !== 'null' &&
        String(pcName).toLowerCase() !== 'undefined';

      // If PC name or ID is null/empty, do NOT count that profit center
      if (hasValidName || hasValidId) {
        const key = hasValidName ? String(pcName).trim() : String(pcId).trim();
        if (!map.has(key)) {
          map.set(key, hasValidName ? String(pcName).trim() : String(pcId).trim());
        }
      }
    });
    return Array.from(map.values());
  }, [rawGridDetails]);

  const corpDisplayLabel = useMemo(() => {
    if (uniqueCorps.length > 1) {
      return `${uniqueCorps.length} Corporations selected`;
    }
    if (uniqueCorps.length === 1) {
      return uniqueCorps[0];
    }
    if (corporation && corporation !== 'all' && !corporation.includes('Corporations Selected')) {
      return corporation;
    }
    return '-';
  }, [uniqueCorps, corporation]);

  const pcDisplayLabel = useMemo(() => {
    if (uniquePcs.length > 1) {
      return `${uniquePcs.length} Profit centers selected`;
    }
    if (uniquePcs.length === 1) {
      return uniquePcs[0];
    }
    return '-';
  }, [uniquePcs]);

  const pcNoDisplayLabel = useMemo(() => {
    if (uniquePcs.length === 0) return '-';
    if (uniquePcs.length > 1) {
      return 'Multiple';
    }
    if (rawGridDetails.length > 0) {
      const match = rawGridDetails.find((r) => {
        const val = r.PCNo ?? r.pcNo ?? r.PCNumber ?? r.ProfitCenterNo;
        return val !== undefined && val !== null && String(val).trim() !== '' && String(val).toLowerCase() !== 'null';
      });
      if (match) {
        return String(match.PCNo ?? match.pcNo ?? match.PCNumber ?? match.ProfitCenterNo).trim();
      }
    }
    return '-';
  }, [uniquePcs.length, rawGridDetails]);

  const businessDisplayLabel = useMemo(() => {
    if (rawGridDetails && rawGridDetails.length > 0) {
      const uniqueBrands = [
        ...new Set(
          rawGridDetails
            .map((r) => r?.Brand ?? r?.brand ?? r?.BrandName ?? r?.brandName ?? r?.Business ?? r?.business ?? '')
            .map((b) => String(b).trim())
            .filter((b) => b !== '' && b.toLowerCase() !== 'null' && b.toLowerCase() !== 'undefined')
        ),
      ];

      if (uniqueBrands.length === 1) {
        return uniqueBrands[0];
      }
      if (uniqueBrands.length > 1) {
        return 'Multiple';
      }
    }

    return '-';
  }, [rawGridDetails]);

  const reportTypeFormatted = useMemo(() => {
    if (reportType === 'daily') return 'Daily';
    if (reportType === 'weekly') return 'Weekly';
    if (reportType === 'monthly') return 'Monthly';
    return reportType.charAt(0).toUpperCase() + reportType.slice(1);
  }, [reportType]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        backgroundColor: '#ffffff',
        border: '1px solid #d9d9d9',
        borderBottom: 'none',
        gap: 2,
        boxSizing: 'border-box',
        p: 0,
      }}
    >
      {/* Left Table - Company & Profit Center Details */}
      <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'auto' } }}>
        <Table sx={{ borderCollapse: 'collapse', width: 'auto' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={cellLabelStyle}>
                {isMultiCorpFromResponse ? 'Corporation Legal Name' : 'Corporation/Legal Name'}
              </TableCell>
              <TableCell sx={cellValueStyle}>{corpDisplayLabel}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellLabelStyle}>Profit Center</TableCell>
              <TableCell sx={cellValueStyle}>{pcDisplayLabel}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={cellLabelStyle}>
                {isMultiCorpFromResponse ? '\u00a0' : 'PC NO'}
              </TableCell>
              <TableCell sx={cellValueStyle}>
                {isMultiCorpFromResponse ? '\u00a0' : pcNoDisplayLabel}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Center - Report Title */}
      <Box
        sx={{
          flex: '1 1 auto',
          textAlign: 'center',
          px: 2,
          py: 1,
        }}
      >
        {isMultiCorpFromResponse ? (
          <>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.15rem',
                fontWeight: 700,
                color: '#111827',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
              }}
            >
              Combined Time and Attendance Report
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                mt: 0.5,
              }}
            >
              View by – {reportTypeFormatted}
            </Typography>
          </>
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              lineHeight: 1.3,
            }}
          >
            {reportTitle}
          </Typography>
        )}
      </Box>

      {/* Right Table - Date Range & Business Details */}
      <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'auto' } }}>
        <Table sx={{ borderCollapse: 'collapse', width: 'auto' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...cellLabelStyle, minWidth: 50 }}>Date</TableCell>
              <TableCell sx={{ ...cellValueStyle, minWidth: 110 }}>{formatDateString(fromDate)}</TableCell>
              <TableCell sx={{ ...cellValueStyle, minWidth: 35, textAlign: 'center', px: 1 }}>To</TableCell>
              <TableCell sx={{ ...cellValueStyle, minWidth: 110 }}>{formatDateString(toDate)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellLabelStyle, minWidth: 50 }}>Business</TableCell>
              <TableCell colSpan={3} sx={cellValueStyle}>
                {businessDisplayLabel}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellLabelStyle, minWidth: 50 }}>&nbsp;</TableCell>
              <TableCell colSpan={3} sx={cellValueStyle}>
                &nbsp;
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>


    </Box>
  );
};

export default memo(ReportHeader);
